import { Runtime } from "./Runtime";
import { RuntimeStore } from "./RuntimeStore";
import { generateId } from "@/util";
import { getNodeType, getNodeLabel, GraphDefinition } from "@/graph/GraphDefinition";
import { logger } from "@/logging";
import type { Endpoint } from "@/nodes/Endpoint";

export function generateNodeId(nodeType: string): string {
  // Strip namespace prefix if present (e.g., 'elem.Synth' -> 'Synth')
  const unqualifiedType = nodeType.includes('.') ? nodeType.split('.').pop()! : nodeType;
  const prefix = unqualifiedType.charAt(0).toLowerCase() + unqualifiedType.slice(1);
  return generateId(prefix);
}

/**
 * Generate a short label prefix from a node type name.
 * Takes first 4-5 chars, stops at camelCase boundary if possible.
 * Examples: ElementarySynthNode -> elem, SynthNode -> synth, Clock -> clock
 */
function getShortPrefix(nodeType: string): string {
  // Remove common suffixes
  let name = nodeType.replace(/Node$/, '');

  // Find a good break point (lowercase followed by uppercase, or after 4-5 chars)
  let prefix = '';
  for (let i = 0; i < name.length && i < 6; i++) {
    prefix += name[i];
    // Stop at camelCase boundary after at least 3 chars
    if (i >= 3 && i < name.length - 1 && /[a-z]/.test(name[i]) && /[A-Z]/.test(name[i + 1])) {
      break;
    }
  }

  return prefix.toLowerCase();
}

/**
 * Generate a short label for a new node based on existing nodes in the definition.
 * Uses the primitive's shortName if available, otherwise derives from nodeType.
 * Returns something like "synth1", "out2", etc.
 */
function generateNodeLabel(nodeType: string, definition: GraphDefinition, runtime: Runtime): string {
  // Use shortName from nodeDef if available, otherwise derive from nodeType
  const nodeDef = runtime.getPluginRegistry().getAllNodeDefs().find(n => n.type === nodeType);
  const prefix = (nodeDef && nodeDef.shortName) ? nodeDef.shortName : getShortPrefix(nodeType);

  // Count existing nodes with labels starting with this prefix
  let maxNum = 0;
  for (const nodeEntry of Object.values(definition.nodes)) {
    const label = getNodeLabel(nodeEntry);
    if (label && label.startsWith(prefix)) {
      const numPart = label.slice(prefix.length);
      const num = parseInt(numPart, 10);
      if (!isNaN(num) && num > maxNum) {
        maxNum = num;
      }
    }
  }

  return `${prefix}${maxNum + 1}`;
}

export const runtimeApi = (runtime: Runtime, store: RuntimeStore) => {
  const isPrimitive = (nodeType: string) => runtime.getPluginRegistry().hasNode(nodeType);

  /** Get all library definition dependencies (recursive) */
  function getLibraryDeps(nodeType: string, deps = new Set<string>()): Set<string> {
    if (isPrimitive(nodeType) || deps.has(nodeType)) return deps;
    const libDef = store.libraryDefinitions[nodeType];
    if (!libDef) return deps;
    deps.add(nodeType);
    for (const nodeEntry of Object.values(libDef.nodes)) {
      getLibraryDeps(getNodeType(nodeEntry), deps);
    }
    return deps;
  }

  /** Throws if any library dependency conflicts with an existing project definition */
  function checkProjectConflicts(libraryNodeType: string): void {
    const libDeps = getLibraryDeps(libraryNodeType);
    for (const dep of libDeps) {
      if (runtime.getGraphDefinition(dep)) {
        throw new Error(`Cannot add '${libraryNodeType}': its dependency '${dep}' conflicts with an existing project definition.`);
      }
    }
  }

  function ensureDefinitionRegistered(nodeType: string): void {
    if (isPrimitive(nodeType)) return;
    if (runtime.getGraphDefinition(nodeType)) {
      logger.trace('runtimeApi', `[ensureDefinitionRegistered] ${nodeType} already registered, skipping`);
      return;
    }

    logger.trace('runtimeApi', `[ensureDefinitionRegistered] ${nodeType} not found, registering from library`);
    const libDef = store.libraryDefinitions[nodeType];
    if (!libDef) {
      throw new Error(`Unknown node type: ${nodeType}`);
    }

    checkProjectConflicts(nodeType);

    // Register all dependencies
    const libDeps = getLibraryDeps(nodeType);
    for (const dep of libDeps) {
      const depDef = store.libraryDefinitions[dep];
      if (depDef) {
        runtime.registerGraphDefinition(depDef);
      }
    }
  }

  function getDefinitionId(instanceId: string): string {
    const instance = runtime.getInstance(instanceId);
    if (instance) return instance.definitionId;
    // Allow passing a definitionId directly (for definition-edit mode with phantom instances)
    if (runtime.getGraphDefinition(instanceId)) return instanceId;
    throw new Error(`Instance not found: ${instanceId}`);
  }

  /** Check if adding nodeType to targetDef would create a recursion (nodeType contains targetDef) */
  function wouldCreateRecursion(nodeType: string, targetDef: string, visited = new Set<string>()): boolean {
    if (isPrimitive(nodeType)) return false;
    if (nodeType === targetDef) return true;
    if (visited.has(nodeType)) return false;
    visited.add(nodeType);

    const def = runtime.getGraphDefinition(nodeType);
    if (!def) return false;

    for (const nodeEntry of Object.values(def.nodes)) {
      const childType = getNodeType(nodeEntry);
      if (wouldCreateRecursion(childType, targetDef, visited)) {
        return true;
      }
    }
    return false;
  }

  return {
    deleteNode(instanceId: string, nodeId: string) {
      const definitionId = getDefinitionId(instanceId);
      runtime.removeNodeFromDefinition(definitionId, nodeId);
    },
    addNode(instanceId: string, nodeType: string): string {
      const definitionId = getDefinitionId(instanceId);
      // Guard against cyclic nesting (direct or indirect)
      if (wouldCreateRecursion(nodeType, definitionId)) {
        throw new Error(`Cannot add '${nodeType}' inside '${definitionId}' - this would cause infinite recursion.`);
      }
      ensureDefinitionRegistered(nodeType);
      const nodeId = generateNodeId(nodeType);
      const definition = runtime.getGraphDefinition(definitionId);
      // Constraint nodes don't get auto-generated labels — they use shortName from buildNodeState
      const isConstraint = nodeType.startsWith('struct.');
      const label = (!isConstraint && definition) ? generateNodeLabel(nodeType, definition, runtime) : undefined;
      runtime.addNodesToDefinition(definitionId, { [nodeId]: { type: nodeType, label } }, [], {});
      return nodeId;
    },
    addNodes(instanceId: string,
      nodes: Record<string, string>,
      connections: Array<{ source: string; target: string }>,
      defaults: Record<string, any> = {}
    ) {
      const definitionId = getDefinitionId(instanceId);
      for (const nodeType of Object.values(nodes)) {
        // Guard against cyclic nesting (direct or indirect)
        if (wouldCreateRecursion(nodeType, definitionId)) {
          throw new Error(`Cannot add '${nodeType}' inside '${definitionId}' - this would cause infinite recursion.`);
        }
        ensureDefinitionRegistered(nodeType);
      }
      runtime.addNodesToDefinition(definitionId, nodes, connections, defaults);
    },
    addConnection(instanceId: string, source: string, target: string) {
      const definitionId = getDefinitionId(instanceId);
      const result = runtime.addInternalConnection(definitionId, source, target);
      if (!result.added) {
        throw new Error(`Failed to add connection ${source} -> ${target}: ${result.message}`);
      }
    },
    removeConnection(instanceId: string, source: string, target: string) {
      const definitionId = getDefinitionId(instanceId);
      const result = runtime.removeInternalConnection(definitionId, source, target);
      if (!result.removed) {
        throw new Error(`Failed to remove connection ${source} -> ${target}: ${result.message}`);
      }
    },
    toggleConnectionDisabled(instanceId: string, connectionId: string): void {
      const definitionId = getDefinitionId(instanceId);
      const currentlyDisabled = runtime.isConnectionMuted(definitionId, connectionId);
      runtime.setConnectionDisabled(definitionId, connectionId, !currentlyDisabled);
    },
    setNodeLogLevel(instanceId: string, nodeId: string, logLevel: string) {
      runtime.setNodeLogLevel(instanceId, nodeId, logLevel)
    },
    setNodeLabel(instanceId: string, nodeId: string, label: string | undefined) {
      const definitionId = getDefinitionId(instanceId);
      runtime.setNodeLabel(definitionId, nodeId, label);
    },
    setNodeCollapsed(instanceId: string, nodeId: string, isCollapsed: boolean) {
      const definitionId = getDefinitionId(instanceId);
      store.setNodeCollapsed(definitionId, nodeId, isCollapsed);
    },
    // @param endpointKey - Pre-built key in format "nodeId.endpointId" 
    //NB ONLY FOR GETTING VALUES FOR AUDIO ENDPOINT LEVELS. ALL OTHER READS FROM STORE
    getEndpointValue(instanceId: string, endpointKey: string) {
      return runtime.getEndpointValue(instanceId, endpointKey);
    },
    setEndpointMetadata(instanceId: string, nodeId: string, endpointId: string, field: string, value: any): void {
      const definitionId = getDefinitionId(instanceId);
      runtime.setGraphConfig(definitionId, nodeId, endpointId, field, value);
    },
    tweakValue(instanceId: string, nodeId: string, endpointId: string, value: any): void {
      const definitionId = getDefinitionId(instanceId);
    //  if (definitionId !== 'root') {
        runtime.setGraphConfig(definitionId, nodeId, endpointId, 'value', value);
    //  } else {
   //     runtime.setValue(instanceId, `${nodeId}.${endpointId}`, value);
    //  }
    },
    /** Tweak a value by definition ID (no instance required) */
    tweakDefinition(definitionId: string, nodeId: string, endpointId: string, value: any): void {
      runtime.setGraphConfig(definitionId, nodeId, endpointId, 'value', value);
    },
    /** Get config value for a definition's node endpoint */
    getDefinitionValue(definitionId: string, nodeId: string, endpointId: string): any {
      const def = runtime.getGraphDefinition(definitionId);
      if (!def) return undefined;
      const nodeConfig = def.config[nodeId];
      if (!nodeConfig) return undefined;
      const entry = nodeConfig[endpointId];
      if (entry === undefined) return undefined;
      // Config can be compact (raw value) or full ({value, ...})
      if (typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry) {
        return entry.value;
      }
      return entry;
    },
    /** Fire a trigger endpoint without persisting (for UI trigger buttons) */
    fireTrigger(instanceId: string, nodeId: string, endpointId: string): void {
      runtime.setInternalValue(instanceId, nodeId, endpointId, true);
    },
    /** Get exposed endpoints that would be affected by encapsulation */
    getAffectedExposedEndpoints(instanceId: string, nodeIds: string[]): string[] {
      const definitionId = getDefinitionId(instanceId);
      const def = runtime.getGraphDefinition(definitionId);
      if (!def) return [];

      const selectedSet = new Set(nodeIds);
      const affected: string[] = [];
      for (const [exposedName, exposed] of Object.entries(def.api)) {
        const [pathNodeId] = exposed.path.split('.');
        if (selectedSet.has(pathNodeId)) {
          affected.push(exposedName);
        }
      }
      return affected;
    },
    encapsulate(
      instanceId: string,
      nodeIds: string[],
      newDefinitionId: string,
      options: { newInstanceId?: string; preserveContainerApi?: boolean } = {}
    ): string {
      const { preserveContainerApi = true } = options;
      // Check for definition name collision
      if (runtime.getGraphDefinition(newDefinitionId)) {
        throw new Error(`Definition '${newDefinitionId}' already exists. Please choose a different name.`);
      }
      const definitionId = getDefinitionId(instanceId);
      // Generate unique instance ID if not provided (nodeId in parent must match instanceId for navigation)
      const newInstanceId = options.newInstanceId || generateNodeId(newDefinitionId);
      runtime.encapsulate(definitionId, nodeIds, newDefinitionId, newInstanceId, preserveContainerApi);
      return newInstanceId;
    },
    hasDefinition(id: string): boolean {
      return runtime.getGraphDefinition(id) !== undefined;
    },
    exposeEndpoint(instanceId: string, internalPath: string) {
      // Update runtime immediately (rebuilds virtual endpoints on running instances)
      const definitionId = getDefinitionId(instanceId);
      runtime.exposeEndpoint(definitionId, internalPath);
    },
    unexposeEndpoint(instanceId: string, internalPath: string): void {
      // Perform unexpose locally - returns modified parent definition IDs
      const definitionId = getDefinitionId(instanceId);
      runtime.unexposeEndpoint(definitionId, internalPath);
    },
    clear(): void {
      // Clear local runtime - reset to empty root
      const emptyRoot = {
        id: 'root',
        nodes: {},
        connections: [],
        api: {},
        config: {}
      };
      runtime.setState({ definitions: { root: emptyRoot } });
    },
    getInstanceDefinitionId(instanceId: string): string {
      return runtime.getInstance(instanceId)?.definitionId ?? instanceId;
    },

    /**
     * Set the visualization node for a definition (subgraph).
     * This determines which internal node provides the visualization for the subgraph.
     */
    setVizNode(definitionId: string, nodeId: string | null): void {
      runtime.setVizNode(definitionId, nodeId);
    },

    /**
     * Get the number of instances of a definition.
     */
    getInstanceCount(definitionId: string): number {
      return runtime.getInstancesOfGraph(definitionId).length;
    },

    /**
     * Compare two definition APIs and return removed endpoint names.
     * Used to warn users about potential disconnections when reloading from library.
     */
    getApiDifferences(currentDef: GraphDefinition, newDef: GraphDefinition): {
      removed: string[];
      added: string[];
    } {
      const currentNames = new Set(Object.keys(currentDef.api ?? {}));
      const newNames = new Set(Object.keys(newDef.api ?? {}));

      const removed: string[] = [];
      const added: string[] = [];

      for (const name of currentNames) {
        if (!newNames.has(name)) {
          removed.push(name);
        }
      }
      for (const name of newNames) {
        if (!currentNames.has(name)) {
          added.push(name);
        }
      }

      return { removed, added };
    },

    /**
     * Reload a definition from the library, replacing the current project version.
     * Also rebuilds all existing instances to pick up the new structure.
     * Removes connections to endpoints that no longer exist.
     */
    reloadDefinitionFromLibrary(definitionId: string): void {
      const libraryDef = store.libraryDefinitions[definitionId];
      if (!libraryDef) {
        throw new Error(`Definition '${definitionId}' not found in library`);
      }

      const currentDef = runtime.getGraphDefinition(definitionId);
      const newApiNames = new Set(Object.keys(libraryDef.api ?? {}));

      // Find and remove connections that reference removed endpoints
      if (currentDef) {
        const removedEndpoints = Object.keys(currentDef.api ?? {}).filter(name => !newApiNames.has(name));

        if (removedEndpoints.length > 0) {
          // Find all definitions that have nodes of this type
          for (const [parentDefId, parentDef] of Object.entries(runtime.getAllGraphDefinitions())) {
            const nodesOfType = Object.entries(parentDef.nodes)
              .filter(([_, nodeEntry]) => getNodeType(nodeEntry) === definitionId)
              .map(([nodeId]) => nodeId);

            if (nodesOfType.length === 0) continue;

            // Remove connections that reference removed endpoints on these nodes
            const connectionsToRemove: Array<{ source: string; target: string }> = [];
            for (const conn of parentDef.connections) {
              for (const nodeId of nodesOfType) {
                for (const removedEp of removedEndpoints) {
                  const epPath = `${nodeId}.${removedEp}`;
                  if (conn.source === epPath || conn.target === epPath) {
                    connectionsToRemove.push(conn);
                    logger.info('runtimeApi', `Removing connection ${conn.source} -> ${conn.target} (endpoint '${removedEp}' removed from ${definitionId})`);
                  }
                }
              }
            }

            // Remove the connections from the definition
            for (const conn of connectionsToRemove) {
              const idx = parentDef.connections.findIndex(c => c.source === conn.source && c.target === conn.target);
              if (idx !== -1) {
                parentDef.connections.splice(idx, 1);
              }
            }
          }
        }
      }

      // Deep copy to avoid mutating library version
      const defCopy = JSON.parse(JSON.stringify(libraryDef)) as GraphDefinition;
      runtime.registerGraphDefinition(defCopy);
      // Rebuild runtime from definitions to pick up the updated definition
      runtime.rebuildFromDefinitions();
    },

    /**
     * Get a definition from the library (not the project version).
     */
    getLibraryDefinition(definitionId: string): GraphDefinition | undefined {
      return store.libraryDefinitions[definitionId];
    },

    /**
     * Update a library definition in the local cache.
     * Called after saving to server to keep store in sync.
     */
    setLibraryDefinition(definition: GraphDefinition): void {
      store.libraryDefinitions[definition.id] = definition;
    },

    /**
     * Add a custom param to a node's config.
     * Only valid for nodes with allowAddParam: true.
     * The param will be processed by the params() helper for sensible defaults.
     */
    addCustomParam(instanceId: string, nodeId: string, paramId: string, config: { value?: any; dataType?: string; dataFlow?: string; label?: string } = {}): void {
      const definitionId = getDefinitionId(instanceId);
      runtime.addEndpoint(definitionId, nodeId, paramId, config as Partial<Endpoint>);
    },

    /**
     * Remove a custom param from a node's config.
     */
    removeCustomParam(instanceId: string, nodeId: string, paramId: string): void {
      const definitionId = getDefinitionId(instanceId);
      runtime.removeEndpoint(definitionId, nodeId, paramId);
    },

    /**
     * Call a method on a node instance.
     * Used by UI components to interact with nodes (e.g., passing canvas to Hydra).
     */
    /**
     * Create a temporary "phantom" instance of a definition for editing/testing.
     * Returns the phantom instanceId (uses ~def/ prefix convention).
     */
    createPhantomInstance(definitionId: string): string {
      const phantomId = `~def/${definitionId}`;
      // Already exists — reuse
      if (runtime.getInstance(phantomId)) return phantomId;
      if (!runtime.getGraphDefinition(definitionId)) {
        throw new Error(`Definition '${definitionId}' not found`);
      }
      runtime.instantiate(definitionId, phantomId);
      return phantomId;
    },

    /**
     * Dispose a phantom instance created via createPhantomInstance.
     */
    disposePhantomInstance(definitionId: string): void {
      const phantomId = `~def/${definitionId}`;
      if (runtime.getInstance(phantomId)) {
        runtime.disposeInstance(phantomId);
      }
    },

    callNodeMethod(instanceId: string, nodeId: string, method: string, ...args: any[]): any {
      const instance = runtime.getInstance(instanceId);
      if (!instance) {
        throw new Error(`Instance not found: ${instanceId}`);
      }
      let node = instance.nodes[nodeId];
      if (!node) {
        throw new Error(`Node not found: ${nodeId} in instance ${instanceId}`);
      }

      // For subgraph nodes, check if there's a vizNode to route method calls to
      if ('nodes' in node) {
        const subInstance = node as any;
        const subDef = runtime.getGraphDefinition(subInstance.definitionId);
        logger.trace('runtimeApi', `[callNodeMethod] subgraph detected, definitionId: ${subInstance.definitionId}, vizNode: ${subDef?.vizNode}`);
        if (subDef?.vizNode) {
          const internalNode = subInstance.nodes[subDef.vizNode];
          logger.trace('runtimeApi', `[callNodeMethod] internalNode: ${internalNode ? 'found' : 'NOT FOUND'}, available nodes: ${Object.keys(subInstance.nodes).join(', ')}`);
          if (internalNode) {
            node = internalNode;
          }
        }
      }

      const fn = (node as any)[method];
      if (typeof fn !== 'function') {
        throw new Error(`Method '${method}' not found on node ${nodeId}`);
      }
      return fn.call(node, ...args);
    }
  }
}